import React from "react";
import {
  Page,
  Stack,
  TextField,
  Text,
  Divider,
  Button,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { Controller, useFormContext } from "react-hook-form";
import { Spacings, View, Colors, TouchableOpacity } from "react-native-ui-lib";
import { GenerateAiIconFormValues } from "../../../../components/GenerateAiIconForm";
import {
  ExecuteLlmQuickWinResult,
  LlmImageStyle,
} from "@towersystems/roam-common/lib/generated-types";
import { StylesGrid } from "../../../../components/StylesGrid";
import { useMutation } from "@apollo/client";
import { MUTATION_EXECUTE_QUICK_WIN } from "../../../../utilities/llm-quick-wins";
import { Ionicons } from "@expo/vector-icons";
import { GenerateAiIconRootParamList } from "../../../../types";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { iconStyles } from "./common";

export type GenerateIconScreenProps = NativeStackScreenProps<
  GenerateAiIconRootParamList,
  "GenerateIconScreen"
>;

export const GenerateIconScreen = ({ navigation }: GenerateIconScreenProps) => {
  const [generatingIcon, setGeneratingIcon] = React.useState(false);
  const { control, watch, setValue, getValues, formState } =
    useFormContext<GenerateAiIconFormValues>();
  const flashMessagesContext = useFlashMessages();

  const { isSubmitting, isValid } = formState;
  const watchedStylesLength = watch("style")?.length || 0;
  const watchedColorTheme = watch("colorScheme");

  const [m] = useMutation<{
    executeLlmQuickWin: ExecuteLlmQuickWinResult;
  }>(MUTATION_EXECUTE_QUICK_WIN);

  const handleOnGenerateIcon = async () => {
    setGeneratingIcon(true);
    const values = getValues();

    const allBase64Images = await Promise.all(
      values.style.map(async (style) => {
        try {
          const response = await m({
            variables: {
              input: {
                generate_icon_quick_win: {
                  description: values.description,
                  style: style as LlmImageStyle,
                  colorScheme: values.colorScheme,
                },
              },
            },
          });
          const result = response.data?.executeLlmQuickWin;
          if (result && "result" in result) {
            return result.result;
          } else {
            if (result && "message" in result) {
              throw new Error(result.message);
            }
          }
        } catch (error: any) {
          return undefined;
        }
      })
    );

    for (const img of allBase64Images) {
      if (img) {
        setValue("generatedBase64Images", [
          ...(getValues().generatedBase64Images || []),
          img,
        ]);
      }
    }

    navigation.navigate("PreviewIconScreen");
  };

  const footerMarkup = (
    <View row>
      <View flex>
        <Button
          disabled={
            isSubmitting ||
            !isValid ||
            generatingIcon ||
            watchedStylesLength === 0
          }
          label={
            watchedStylesLength === 0
              ? "Select at least one style"
              : generatingIcon
              ? "Generating..."
              : `Generate ${watchedStylesLength} Icon${
                  watchedStylesLength > 1 ? "s" : ""
                }`
          }
          isAction
          onPress={handleOnGenerateIcon}
          icon={<Ionicons name="sparkles" size={17} color={Colors.action} />}
        ></Button>
      </View>
    </View>
  );

  return (
    <Page
      title="Generate Icon"
      secondaryTitle="Generate an AI icon"
      footer={footerMarkup}
    >
      <Stack vertical>
        <View>
          <View style={{ marginBottom: Spacings.s5 }}>
            <Text variant="heading4" fontWeight="bold">
              Describe the icon
            </Text>
            <Text variant="heading4" color="subdued">
              Provide a brief description of the icon you want to generate.
            </Text>
          </View>

          <Controller
            name="description"
            control={control}
            render={({
              field: { onChange, value, onBlur, disabled },
              fieldState: { error },
            }) => (
              <TextField
                onBlur={onBlur}
                value={value ? String(value) : ""}
                readonly={disabled}
                onChangeText={(value) => onChange(value)}
                validationMessage={error ? error.message : undefined}
                placeholder={"e.g., A red apple with a bite taken out"}
              />
            )}
          />
        </View>

        <Divider marginVertical={0} />
        <View>
          <View style={{ marginBottom: Spacings.s5 }}>
            <Text variant="heading4" fontWeight="bold">
              Select Color Theme
            </Text>
            <Text variant="heading4" color="subdued">
              Choose a color theme for your icon.
            </Text>
          </View>
          <View>
            <TouchableOpacity
              style={{
                padding: Spacings.s4,
                width: 100,
                height: 100,
                borderRadius: 8,
                backgroundColor: watchedColorTheme,
                borderWidth: 2,
                borderColor: "#CCCCCC15",
              }}
              onPress={() => navigation.navigate("PickIconThemeScreen")}
            ></TouchableOpacity>
          </View>
        </View>

        <Divider marginVertical={0} />
        <View>
          <View style={{ marginBottom: Spacings.s5 }}>
            <Text variant="heading4" fontWeight="bold">
              Select styles
            </Text>
            <Text variant="heading4" color="subdued">
              Choose one or more styles for your icon.
            </Text>
          </View>
          <View>
            <StylesGrid
              multiSelect
              styles={iconStyles}
              selectedStyles={watch("style")}
              onSelect={(val) => setValue("style", val)}
              itemSpacing={32}
              itemVerticalMargin={12}
            />
          </View>
        </View>
      </Stack>
    </Page>
  );
};
